import { FileCheck } from 'lucide-react';
import Table from '@/components/table/components/Table.tsx';
import EmptyTable from '@/components/table/components/EmptyTable.tsx';
import { RowContextMenuItemParams } from '@/components/table/components/RowContextMenu.tsx';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { useConfirmationDialogContext } from '@/components/ui/ConfirmDialog.Context.tsx';
import bookingAcceptedConsentsApi from '@/features/dashboard/bookings/api/bookingAcceptedConsentsApi.ts';
import { bookingsApiQueryKeys } from '@/features/dashboard/bookings/api/bookingsApi.queryKeys.ts';
import { AcceptedConsentListDto } from '@/features/dashboard/clients/types/AcceptedConsentListDto.ts';
import { ConsentStatus } from '@/features/dashboard/consentTemplates/types/ConsentStatus.ts';
import useWithdrawAcceptedConsent from '@/features/dashboard/acceptedConsents/hooks/useWithdrawAcceptedConsent.ts';
import { useDetailsScreen } from '@/features/dashboard/bookingChanges/contexts/DetailsContext.ts';
import SendConsentRequestButton from '@/features/dashboard/bookings/components/SendConsentRequestButton.tsx';
import { useGetMissingRequiredConsents } from '@/features/dashboard/bookings/hooks/useGetMissingRequiredConsents.ts';
import useGetBookingDetails from '@/features/dashboard/bookings/hooks/useGetBookingDetails.ts';
import { BookingStatus } from '@/types/BookingStatus.ts';

/** Zakładka „Zgody” ekranu rezerwacji — co ta rezerwacja ma podpisane. */
const BookingConsentsTab = () => {
  const { bookingId } = useDetailsScreen();
  const { showDialog } = useConfirmationDialogContext();
  const withdrawAcceptedConsent = useWithdrawAcceptedConsent({ bookingId });
  // Osobny odczyt, bo lista zgód domyślnie chowa niepodpisane i dubluje wiersze po ponownym
  // podpisie, więc nie mówi wiarygodnie, czego brakuje.
  const { data: missingRequiredConsents } =
    useGetMissingRequiredConsents(bookingId);
  const missingDisplayNames = missingRequiredConsents?.displayNames ?? [];
  // Nieznany jeszcze stan to brak przycisku — nie mignie akcja, której backend odmówi.
  const { data: booking } = useGetBookingDetails(bookingId);
  const bookingActive = booking?.status === BookingStatus.ACTIVE;

  const rowContextMenuItems: RowContextMenuItemParams<AcceptedConsentListDto>[] =
    [
      {
        label: 'Wycofaj zgodę',
        variant: 'destructive',
        disabled: (row) =>
          row.consentStatus !== ConsentStatus.ACCEPTED ||
          row.booking?.status === BookingStatus.CANCELLED,
        action: async (row) => {
          const confirmed = await showDialog({
            title: 'Wycofaj zgodę',
            description:
              'Czy na pewno chcesz wycofać tę zgodę? Tej operacji nie można cofnąć — aby zgoda znów była aktywna, konieczne będzie ponowne wyrażenie jej przez klienta.',
            variant: 'destructive',
            confirmText: 'Wycofaj zgodę',
          });
          if (confirmed) {
            withdrawAcceptedConsent.mutate(row.id);
          }
        },
      },
    ];

  return (
    <Table
      queryKey={bookingsApiQueryKeys.acceptedConsents(bookingId)}
      fetchData={(pr, fc: FilterCriteriaDto) =>
        bookingAcceptedConsentsApi.getAll(bookingId, pr, fc)
      }
      fetchTableDef={() =>
        bookingAcceptedConsentsApi.getTableDefinition(bookingId)
      }
      rowContextMenuItems={rowContextMenuItems}
      // Tabela rysuje separator przy każdym zdefiniowanym `primaryAction`, także gdy komponent zwraca null.
      primaryAction={
        missingDisplayNames.length > 0 && bookingActive ? (
          <SendConsentRequestButton
            bookingId={bookingId}
            displayNames={missingDisplayNames}
          />
        ) : undefined
      }
      emptyDataComponent={
        <EmptyTable
          title="Brak zgód do tej rezerwacji"
          description="Wyjazd nie ma jeszcze przypiętych zgód albo zapisujący nie zdążył ich wyrazić."
          icon={<FileCheck />}
        />
      }
    />
  );
};

export default BookingConsentsTab;
